/**
 * Frontend metric group registry — mirrors backend/src/config/metricGroups.ts
 *
 * Maps group IDs to their metric keys and display names.
 * Used to dynamically resolve METRIC_TIERS and TIER_DISPLAY_CONFIG
 * based on the `selectedGroups` field from the API response.
 */

export interface MetricGroupMetric {
  key: string;           // key used in METRIC_TIERS (e.g. "operatingMargin")
  formattedKey: string;  // key in FormattedFinancialMetrics for value lookup
}

export interface MetricGroupDefinition {
  id: string;
  displayName: string;
  metrics: [MetricGroupMetric, MetricGroupMetric, MetricGroupMetric];
}

// ─── Fixed Groups ──────────────────────────────────────────────────────

const BASICS: MetricGroupDefinition = {
  id: 'basics',
  displayName: 'Basics',
  metrics: [
    { key: 'annualRevenue', formattedKey: 'annualRevenue' },
    { key: 'quarterlyRevenueGrowth', formattedKey: 'quarterlyRevenueGrowth' },
    { key: 'dilutedEps', formattedKey: 'dilutedEps' },
  ],
};

const VALUATION: MetricGroupDefinition = {
  id: 'valuation',
  displayName: 'Valuation',
  metrics: [
    { key: 'trailingPE_combined', formattedKey: 'trailingPE' },
    { key: 'priceToSales_combined', formattedKey: 'priceToSales' },
    { key: 'marketCapitalization', formattedKey: 'marketCap' },
  ],
};

// ─── Dynamic Groups ────────────────────────────────────────────────────

const PROFITABILITY: MetricGroupDefinition = {
  id: 'profitability',
  displayName: 'Profitability',
  metrics: [
    { key: 'netIncome', formattedKey: 'netIncome' },
    { key: 'leveredFreeCashFlow', formattedKey: 'leveredFreeCashFlow' },
    { key: 'fiveYearDividendYield', formattedKey: 'fiveYearDividendYield' },
  ],
};

const BALANCE_SHEET: MetricGroupDefinition = {
  id: 'balance-sheet',
  displayName: 'Balance Sheet',
  metrics: [
    { key: 'cash', formattedKey: 'cash' },
    { key: 'debt', formattedKey: 'debt' },
    { key: 'debtToEquity', formattedKey: 'debtToEquity' },
  ],
};

const MARGINS: MetricGroupDefinition = {
  id: 'margins',
  displayName: 'Margins',
  metrics: [
    { key: 'grossMargin', formattedKey: 'grossMargin' },
    { key: 'operatingMargin', formattedKey: 'operatingMargin' },
    { key: 'profitMargin', formattedKey: 'profitMargin' },
  ],
};

const CASH_FLOW: MetricGroupDefinition = {
  id: 'cash-flow',
  displayName: 'Cash Flow',
  metrics: [
    { key: 'operatingCashFlow', formattedKey: 'operatingCashFlow' },
    { key: 'revenuePerShare', formattedKey: 'revenuePerShare' },
    { key: 'bookValuePerShare', formattedKey: 'bookValuePerShare' },
  ],
};

const VALUE: MetricGroupDefinition = {
  id: 'value',
  displayName: 'Value',
  metrics: [
    { key: 'forwardPE', formattedKey: 'forwardPE' },
    { key: 'priceToBook', formattedKey: 'priceToBook' },
    { key: 'evToRevenue', formattedKey: 'evToRevenue' },
  ],
};

const GROWTH: MetricGroupDefinition = {
  id: 'growth',
  displayName: 'Growth',
  metrics: [
    { key: 'pegRatio', formattedKey: 'pegRatio' },
    { key: 'earningsGrowthYoY', formattedKey: 'earningsGrowthYoY' },
    { key: 'enterpriseValue', formattedKey: 'enterpriseValue' },
  ],
};

const OWNERSHIP: MetricGroupDefinition = {
  id: 'ownership',
  displayName: 'Ownership',
  metrics: [
    { key: 'institutionalOwnership', formattedKey: 'institutionalOwnership' },
    { key: 'insiderOwnership', formattedKey: 'insiderOwnership' },
    { key: 'shortRatio', formattedKey: 'shortRatio' },
  ],
};

// ─── Registry ──────────────────────────────────────────────────────────

export const METRIC_GROUP_REGISTRY: Record<string, MetricGroupDefinition> = {
  basics: BASICS,
  valuation: VALUATION,
  profitability: PROFITABILITY,
  'balance-sheet': BALANCE_SHEET,
  margins: MARGINS,
  'cash-flow': CASH_FLOW,
  value: VALUE,
  growth: GROWTH,
  ownership: OWNERSHIP,
};

/** Legacy fallback groups when selectedGroups is absent (old cached data) */
export const LEGACY_DYNAMIC_GROUPS: [string, string] = ['profitability', 'balance-sheet'];

/**
 * Resolve the 4 tier metric keys and display names based on selectedGroups.
 *
 * Returns: [basics, dynamic1, dynamic2, valuation]
 */
export function resolveTiers(selectedGroups?: [string, string] | null): {
  tierMetrics: [string[], string[], string[], string[]];
  tierLabels: [string, string, string, string];
} {
  const dynamic1Id = selectedGroups?.[0] ?? LEGACY_DYNAMIC_GROUPS[0];
  const dynamic2Id = selectedGroups?.[1] ?? LEGACY_DYNAMIC_GROUPS[1];

  const dynamic1 = METRIC_GROUP_REGISTRY[dynamic1Id] ?? METRIC_GROUP_REGISTRY[LEGACY_DYNAMIC_GROUPS[0]];
  const dynamic2 = METRIC_GROUP_REGISTRY[dynamic2Id] ?? METRIC_GROUP_REGISTRY[LEGACY_DYNAMIC_GROUPS[1]];

  return {
    tierMetrics: [
      BASICS.metrics.map(m => m.key),
      dynamic1.metrics.map(m => m.key),
      dynamic2.metrics.map(m => m.key),
      VALUATION.metrics.map(m => m.key),
    ],
    tierLabels: [
      BASICS.displayName,
      dynamic1.displayName,
      dynamic2.displayName,
      VALUATION.displayName,
    ],
  };
}
